import { useRef, useState } from "react";
import { Button, Drawer, Hairline, Input } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { TranslationEditor } from "./TranslationEditor.js";
import type { BrandDTO } from "@wmart/shared-types";

// Same base the api-client uses, so the authed image-serve URL resolves to the
// API host in prod (admin.wmart.uk → api.wmart.uk) and via the Vite proxy in dev.
const API_BASE: string = (import.meta.env.VITE_API_URL as string) ?? "";

/**
 * Create / edit a brand. Flat (no parent) — slug/title/position — plus a logo
 * upload (edit mode only, since the upload endpoint needs the brand id). The
 * storefront renders the logo from logo_key; here it previews via the admin
 * image-serve endpoint.
 */
export function BrandEditDrawer({
  brand,
  onClose,
  onSaved,
}: {
  brand: BrandDTO | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify, notifyProblem } = useToast();
  const [slug, setSlug] = useState(brand?.slug ?? "");
  const [title, setTitle] = useState(brand?.title ?? "");
  const [position, setPosition] = useState(String(brand?.position ?? 0));
  const [logoKey, setLogoKey] = useState<string | null>(brand?.logo_key ?? null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { slug, title, position: Number(position) || 0 };
      if (brand) {
        await api.patch(`/v1/admin/brands/${brand.id}`, payload);
        notify("success", "Brand updated");
      } else {
        await api.post("/v1/admin/brands", payload);
        notify("success", "Brand created");
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(file: File) {
    if (!brand) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const updated = await api.upload<BrandDTO>(
        `/v1/admin/brands/${brand.id}/logo`,
        fd,
      );
      setLogoKey(updated.logo_key);
      notify("success", "Logo uploaded");
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Logo upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function removeLogo() {
    if (!brand) return;
    try {
      const updated = await api.delete<BrandDTO>(
        `/v1/admin/brands/${brand.id}/logo`,
      );
      setLogoKey(updated.logo_key);
      notify("success", "Logo removed");
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Logo remove failed");
    }
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={brand ? `Edit ${brand.title}` : "New brand"}
    >
      <form onSubmit={save} className="flex flex-col gap-4 p-6">
        <Input
          label="Slug"
          required
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="font-mono"
        />
        <Input
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Input
          label="Position"
          type="number"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />

        <div className="flex flex-col gap-2">
          <span className="text-body-sm font-medium text-ink-700">Logo</span>
          {brand ? (
            <>
              {logoKey ? (
                <img
                  src={`${API_BASE}/v1/admin/images/${logoKey}`}
                  alt={`${title} logo`}
                  className="h-20 w-auto max-w-[160px] rounded-sm border border-ink-200 bg-white object-contain p-2"
                />
              ) : (
                <p className="text-body-sm text-ink-500">No logo yet.</p>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadLogo(f);
                  e.target.value = "";
                }}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={uploading}
                  loading={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {logoKey ? "Replace logo" : "Upload logo"}
                </Button>
                {logoKey ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void removeLogo()}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            </>
          ) : (
            <p className="text-body-sm text-ink-500">
              Save the brand first, then add a logo.
            </p>
          )}
        </div>

        <Hairline />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy} loading={busy}>
            Save
          </Button>
        </div>
        {brand && (
          <div className="mt-6 border-t border-ink-100 pt-4">
            <h3 className="text-body font-medium mb-3">Translations</h3>
            <TranslationEditor kind="brand" id={brand.id} />
          </div>
        )}
      </form>
    </Drawer>
  );
}
